import { shallowEqual, useSelector } from "react-redux";

import { NumericFacetBounds } from "@/components/Search/SearchResults/components/results/components/productsResults/components/facets/components/facet/@types/numericFacet.types";
import {
    $SearchModuleName,
    SearchStatus
} from "@/redux/modules/value/search/search.types";
import { IState } from "@/redux/store.types";
import { getValueState } from "@/redux/utils";
import { getHierarchicalFacetName } from "@/services/bffSearch/responseTransformers/mergeHierarchicalFacets.transformer";
import {
    SearchComponentEnum,
    SearchComponentOfType
} from "@/utils/search/types/searchComponent";
import { SearchComponentConfigurationMap } from "@/utils/search/types/searchConfiguration";
import { SearchComponentStateMap } from "@/utils/search/types/searchState";

const getSearch = (state: IState) => getValueState(state, $SearchModuleName);

const getQuery = (state: IState) => getSearch(state)?.configuration.query ?? "";

const getFilter = (state: IState) =>
    getSearch(state)?.configuration.filters ?? "";

const getSearchConfiguration = (state: IState) =>
    getSearch(state)?.configuration;

const getSearchResults = (state: IState) => getSearch(state)?.results;

const getSearchStatus = (state: IState) =>
    getSearch(state)?.status ?? SearchStatus.Idle;

const getAlgoliaParameters = (state: IState) =>
    getSearch(state)?.algoliaParameters;

const getComponentState =
    <TComponent extends SearchComponentEnum>(component: TComponent) =>
    (state: IState) =>
        getSearch(state)?.results?.find(
            (result) => result.component === component
        ) as SearchComponentStateMap[TComponent] | undefined;

const hasComponentResults = (state: IState) =>
    !!getSearch(state)?.results?.length;

const getComponentConfiguration =
    <TComponent extends SearchComponentEnum>(component: TComponent) =>
    (state: IState) =>
        getSearch(state)?.configuration.components?.find(
            (configuration) => configuration.component === component
        ) as SearchComponentConfigurationMap[TComponent] | undefined;

const getComponentsState =
    <TComponent extends SearchComponentEnum>(components: Array<TComponent>) =>
    (state: IState) =>
        getSearch(state)?.results?.filter((result) =>
            components.some((component) => result.component === component)
        ) as Array<SearchComponentStateMap[TComponent] | undefined>;

const hasResults =
    <TComponent extends SearchComponentOfType<"NUMBER_OF_HITS">>(
        component: TComponent
    ) =>
    (state: IState) =>
        !!getComponentState(component)(state)?.numberOfHits;

const hasAnyComponents =
    <TComponent extends SearchComponentOfType<"NUMBER_OF_HITS">>(
        components: Array<TComponent>
    ) =>
    (state: IState) =>
        getComponentsState(components)(state)?.length;

const hasSelectedFacets =
    <TComponent extends SearchComponentOfType<"FACETS">>(
        component: TComponent
    ) =>
    (state: IState) => {
        const facetConfiguration = getComponentConfiguration(component)(state);
        return (
            !!facetConfiguration?.facetFilters.length ||
            !!facetConfiguration?.numericFilters.length
        );
    };

const getSelectedValuesForFacet =
    <TComponent extends SearchComponentOfType<"FACETS">>(
        component: TComponent,
        facetName: string
    ) =>
    (state: IState) =>
        getComponentConfiguration(component)(state)?.facetFilters.find(
            (facetFilter) => facetFilter.name === facetName
        )?.selectedValues ?? [];

const getSelectedValueForHierarchicalFacet =
    <TComponent extends SearchComponentOfType<"FACETS">>(
        component: TComponent,
        hierarchicalFacetName: string
    ) =>
    (state: IState) =>
        getComponentConfiguration(component)(state)
            ?.facetFilters.filter(
                (facetFilter) =>
                    getHierarchicalFacetName(facetFilter.name) ===
                    hierarchicalFacetName
            )
            .flatMap(
                (hierarchicalFacetFilter) =>
                    hierarchicalFacetFilter.selectedValues
            )
            ?.at(0);

const getSelectedFacetFilters =
    <TComponent extends SearchComponentOfType<"FACETS">>(
        component: TComponent
    ) =>
    (state: IState) =>
        getComponentConfiguration(component)(state)?.facetFilters.filter(
            (facetFilter) => facetFilter.selectedValues.length
        ) ?? [];

const getSelectedNumericFilters =
    <TComponent extends SearchComponentOfType<"FACETS">>(
        component: TComponent
    ) =>
    (state: IState) =>
        getComponentConfiguration(component)(state)?.numericFilters ?? [];

const getBoundsForFacet =
    <TComponent extends SearchComponentOfType<"FACETS">>(
        component: TComponent,
        facetName: string
    ) =>
    (state: IState): NumericFacetBounds => {
        const bounds = getComponentConfiguration(component)(
            state
        )?.numericFilters.find(
            (numericFilter) => numericFilter.name === facetName
        );

        return {
            lowerBound: bounds?.lowerBound,
            upperBound: bounds?.upperBound
        };
    };

const useSearchComponentStateSelector = <
    TComponent extends SearchComponentEnum
>(
    component: TComponent
) => useSelector(getComponentState(component), shallowEqual);

export {
    getAlgoliaParameters,
    getBoundsForFacet,
    getComponentConfiguration,
    getComponentState,
    getComponentsState,
    getFilter,
    getQuery,
    getSearch,
    getSearchConfiguration,
    getSearchResults,
    getSearchStatus,
    getSelectedFacetFilters,
    getSelectedNumericFilters,
    getSelectedValueForHierarchicalFacet,
    getSelectedValuesForFacet,
    hasAnyComponents,
    hasComponentResults,
    hasResults,
    hasSelectedFacets,
    useSearchComponentStateSelector
};
